import { Box, Button, DialogContent, Grid, MenuItem } from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import { verifyError } from "Components/Common/helpers/verifyError";
import { useCompanySetting } from "Components/CompanySetting/hooks/useCompanySetting";
import { useIsInternalAdmin } from "Components/Members/hooks/useIsInternalAdmin";
import ModalActions from "Components/Modal/ModalActions";
import ModalTitle from "Components/Modal/ModalTitle";
import {
	getMembersAlphabetical,
	useIsProjectRedirectable,
} from "Components/Project/ProjectHelper";
import { useProjects } from "Components/Project/hooks/useProjects";
import { useTutorialModal } from "Components/Tutorial/hooks/useTutorialModal";
import ColorField from "Fields/ColorField";
import DatePickerField from "Fields/DatePickerField";
import SelectField from "Fields/SelectField";
import TextField from "Fields/TextField";
import { DEFAULT_COLOR_TAG } from "Helpers/ColorHelper";
import { useRedirect, useRedirectToProject } from "Helpers/RedirectHelper";
import { useApp } from "Providers/AppProvider";
import { useTranslation } from "Providers/TranslationProvider";
import { ValidData } from "Store/types/ValidData";
import { Project, SchedulePhase } from "domain-entities";
import { useSnackbar } from "material-ui-snackbar-provider";
import moment, { Moment } from "moment";
import React, { FC, Fragment } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { useSchedulePhaseModal } from "./hooks/useSchedulePhaseModal";
import { useSchedulePhases } from "./hooks/useSchedulePhases";

interface Props {
	schedulePhase: SchedulePhase;
	project: Project;
	changeProject: () => void;
}

interface FormValues {
	projectName: string;
	projectNoteContent: string;
	projectColorTag: string;
	startDate: Moment | null;
	endDate: Moment | null;
	statusId: string;
}

const SchedulePhaseForm: FC<Props> = (props) => {
	const isInternalAdmin = useIsInternalAdmin();
	const isNewSchedulePhase = useSchedulePhaseModal((state) => state.isNew);
	const companySettingStatuses = useCompanySetting(
		(state) => state.item?.status
	);
	const { isNative } = useApp();
	const { t } = useTranslation();
	const snackbar = useSnackbar();
	const redirect = useRedirect();
	const projects = useProjects((state) => state.projects);
	const schedulePhases = useSchedulePhases((state) => state.items);
	const isProjectRedirectable =
		useIsProjectRedirectable(props.schedulePhase.projectId, projects) &&
		!isNewSchedulePhase;
	const redirectToProject = useRedirectToProject(projects);

	const defaultValues: FormValues = {
		projectName: props.project.name,
		projectNoteContent: props.project.noteContent || "",
		projectColorTag: props.project.colorTag || DEFAULT_COLOR_TAG,
		startDate: moment.unix(props.schedulePhase.startDate),
		endDate: moment.unix(props.schedulePhase.endDate),
		statusId: props.schedulePhase.statusId || "",
	};

	const validationSchema = yup.object().shape({
		projectName: yup.string().required(
			t("Common_Form_Error_MandatoryField", {
				field: t("SchedulePhaseForm_Label_ProjectName"),
			})
		),
		projectColorTag: yup.string().required(),
		startDate: yup
			.object<Moment>()
			.typeError(
				t("Common_Form_Error_MandatoryField", {
					field: t("SchedulePhaseForm_Label_StartDate"),
				})
			)
			.test(
				"test-StartDate",
				t("Common_Form_Error_InvalidDate"),
				function (value) {
					return moment(value).isValid();
				}
			),
		endDate: yup
			.object<Moment>()
			.typeError(
				t("Common_Form_Error_MandatoryField", {
					field: t("SchedulePhaseForm_Label_EndDate"),
				})
			)
			.test(
				"test-EndDate",
				t("Common_Form_Error_InvalidDate"),
				function (value) {
					return moment(value).isValid();
				}
			)
			.required()
			.test(
				"test-endDateBeforeStartDate",
				t("Common_Form_Error_EndDateBeforeStartDate"),
				function (endDate: Moment) {
					if (!moment(this.parent.startDate).isValid()) {
						return true;
					}
					return endDate.isSameOrAfter(this.parent.startDate);
				}
			)
			.test(
				"test-alreadyAssigned",
				t("SchedulePhaseForm_Error_AlreadyAssigned"),
				function (endDate: Moment) {
					const startDate = this.parent.startDate as Moment;

					return !schedulePhases.find(
						(schedulePhase) =>
							schedulePhase.projectId === props.project.id &&
							schedulePhase.id !== props.schedulePhase.id &&
							startDate.isSameOrBefore(moment.unix(schedulePhase.endDate)) &&
							endDate.isSameOrAfter(moment.unix(schedulePhase.startDate))
					);
				}
			),
	});

	const form = useForm<FormValues>({ validationSchema, defaultValues });
	const { control } = form;
	const isDisabled = form.formState.isSubmitting || !isInternalAdmin;
	const isDeleteButtonVisible = isInternalAdmin && !isNewSchedulePhase;
	const isChangeProjectVisible = isInternalAdmin && isNewSchedulePhase;
	const isModalActionVisible =
		isChangeProjectVisible || isDeleteButtonVisible || isProjectRedirectable;

	async function onSubmit(formValues: FormValues) {
		try {
			if (formValues.startDate && formValues.endDate) {
				const nextSchedulePhase: Partial<SchedulePhase> & ValidData = {
					id: props.schedulePhase.id,
					startDate: formValues.startDate.unix(),
					endDate: formValues.endDate.unix(),
					statusId: formValues.statusId || undefined,
				};

				if (isNewSchedulePhase) {
					await useSchedulePhases.getState().setItem({
						...props.schedulePhase,
						...nextSchedulePhase,
					});
				} else {
					await useSchedulePhases.getState().updateItem(nextSchedulePhase);
				}

				const nextProject: Partial<Project> & ValidData = {
					id: props.project.id,
					name: formValues.projectName,
					colorTag: formValues.projectColorTag,
					noteContent: formValues.projectNoteContent || undefined,
					membersAlphabetical: getMembersAlphabetical(
						props.project.members,
						formValues.projectName
					),
				};

				await useProjects.getState().updateProject(nextProject);

				setTimeout(() => {
					if (props.schedulePhase.id) {
						useTutorialModal.getState().openWithItem("dragAndDrop");
					}
				}, 200);

				useSchedulePhaseModal.getState().close();
			} else {
				throw new Error("Missing start or end date");
			}
		} catch (error) {
			snackbar.showMessage(verifyError(error).message);
		}
	}

	function renderStatusItems() {
		return companySettingStatuses?.map((status) => {
			return (
				<MenuItem key={status.id} value={status.id}>
					<Box textAlign="center">{status.name}</Box>
				</MenuItem>
			);
		});
	}

	return (
		<form onSubmit={form.handleSubmit((formValues) => onSubmit(formValues))}>
			<ModalTitle
				close={() => useSchedulePhaseModal.getState().close()}
				title={
					isNewSchedulePhase
						? t("SchedulePhaseForm_Title_Create")
						: t("SchedulePhaseForm_Title_Edit")
				}
			>
				{isInternalAdmin && (
					<Button
						type="submit"
						disabled={isDisabled}
						onClick={form.handleSubmit((formValues) => onSubmit(formValues))}
						variant="contained"
						color="primary"
					>
						{isNewSchedulePhase
							? t("Common_Dialog_Create")
							: t("Common_Dialog_Save")}
					</Button>
				)}
			</ModalTitle>
			<DialogContent>
				<Fragment>
					<Box mt={2}>
						<TextField
							name="projectName"
							label={t("SchedulePhaseForm_Label_ProjectName")}
							control={control}
							disabled={isDisabled}
							autoFocus={!isNative}
						/>
					</Box>
					<Box mt={2}>
						<Grid container spacing={3}>
							<Grid item xs={12} sm={6}>
								<DatePickerField
									name="startDate"
									label={t("SchedulePhaseForm_Label_StartDate")}
									control={control}
									disabled={isDisabled}
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<DatePickerField
									name="endDate"
									label={t("SchedulePhaseForm_Label_EndDate")}
									control={control}
									disabled={isDisabled}
								/>
							</Grid>
						</Grid>
					</Box>
					<Box mt={3}>
						<TextField
							name="projectNoteContent"
							label={t("SchedulePhaseForm_Label_ProjectNoteContent")}
							control={control}
							disabled={isDisabled}
							multiline
						/>
					</Box>
					<Box mt={4}>
						<SelectField
							name="statusId"
							label={t("SchedulePhaseForm_Label_Status")}
							control={control}
							disabled={isDisabled}
						>
							{renderStatusItems()}
						</SelectField>
						{isInternalAdmin && (
							<Box justifyContent="flex-end" display="flex">
								<Button
									color="primary"
									size="small"
									onClick={() => {
										redirect("settings/project-status", true);
									}}
								>
									{t("SchedulePhaseForm_Button_ManageStatus")}
								</Button>
							</Box>
						)}
					</Box>
					<Box mt={2}>
						<ColorField
							name="projectColorTag"
							label={t("SchedulePhaseForm_Label_ProjectColorTag")}
							control={control}
							disabled={isDisabled}
						/>
					</Box>
				</Fragment>
			</DialogContent>
			{isModalActionVisible && (
				<ModalActions>
					{isChangeProjectVisible && (
						<Button
							type="button"
							color="secondary"
							startIcon={<ArrowBackIcon />}
							disabled={isDisabled}
							onClick={props.changeProject}
						>
							{t("SchedulePhaseForm_Action_ChangeProject")}
						</Button>
					)}
					{isDeleteButtonVisible && (
						<Button
							type="button"
							color="secondary"
							startIcon={<DeleteOutlineIcon />}
							disabled={isDisabled}
							onClick={async () => {
								try {
									await useSchedulePhases
										.getState()
										.deleteItem(props.schedulePhase);
									useSchedulePhaseModal.getState().close();
								} catch (error) {
									snackbar.showMessage(verifyError(error).message);
								}
							}}
						>
							{t("SchedulePhaseForm_Action_Delete")}
						</Button>
					)}
					{isProjectRedirectable && (
						<Button
							type="button"
							color="primary"
							startIcon={<OpenInNewIcon />}
							onClick={() => redirectToProject(props.schedulePhase.projectId)}
						>
							{t("Common_Dialog_OpenProject")}
						</Button>
					)}
				</ModalActions>
			)}
		</form>
	);
};

export default SchedulePhaseForm;
